import { useTranslation } from '@supply-mover-2-0/web-ui';
import type { JSX } from 'react';
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { usePortalAuth } from '../hooks/usePortalAuth';
import { useStatusBarSurface } from '../hooks/useStatusBarSurface';

/**
 * PD-16471 — landing page for a tap-to-sign-in link, sent with a sign-in
 * code (by email or text) or in a welcome text. It redeems the one-time
 * token and signs the visitor in; client selection and the rest of the
 * post-sign-in flow are the same as for a password.
 *
 * A link works once, for 15 minutes (24 hours for a welcome text). An
 * expired or used link gets a plain explanation and the way back to the
 * sign-in screen, where the visitor can ask for a new code.
 */
export function SignInLinkPage(): JSX.Element {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const token = searchParams.get('token') ?? '';
	const { loginWithLink } = usePortalAuth();
	// No header band: the status bar reads against this page (PD-16744).
	useStatusBarSurface('page');
	// 'expired' covers an unknown, used or lapsed link (the server answers
	// 401); 'error' is anything else, such as the network dropping.
	const [status, setStatus] = useState<'signing-in' | 'expired' | 'error'>(
		'signing-in',
	);

	// A link can be redeemed only once. React runs effects twice in
	// development, and a second redeem would report the link as used right
	// after the first one signed the visitor in.
	const redeemStarted = useRef(false);

	useEffect(() => {
		if (redeemStarted.current) {
			return;
		}
		redeemStarted.current = true;

		if (token === '') {
			setStatus('expired');
			return;
		}

		void (async (): Promise<void> => {
			try {
				await loginWithLink(token);
				void navigate('/', { replace: true });
			} catch (error) {
				const httpStatus = (error as { status?: unknown }).status;
				setStatus(httpStatus === 401 ? 'expired' : 'error');
			}
		})();
	}, [token, loginWithLink, navigate]);

	if (status === 'signing-in') {
		return (
			<div className="min-h-dvh flex items-center justify-center px-4 py-8 bg-brand-page-bg">
				<p className="text-sm text-brand-muted">{t('Signing you in...')}</p>
			</div>
		);
	}

	return (
		<div className="min-h-dvh flex items-center justify-center px-4 py-8 bg-brand-page-bg">
			<div className="w-full max-w-md bg-brand-surface border border-brand-border rounded-xl p-6 space-y-4">
				<h1 className="text-2xl font-semibold text-default">
					{status === 'expired'
						? t('This sign-in link has expired')
						: t('Something went wrong')}
				</h1>
				<p className="text-sm text-brand-muted">
					{status === 'expired'
						? t(
								'Sign-in links work once and expire after a short time. Sign in again and we will send you a new code.',
							)
						: t(
								'We could not sign you in right now. Please try the link again in a moment.',
							)}
				</p>
				<button
					type="button"
					onClick={() => {
						void navigate('/', { replace: true });
					}}
					className="inline-block rounded-md bg-portal-accent hover:bg-portal-accent-hover text-white font-medium px-4 py-2 transition-colors"
				>
					{t('Go to sign in')}
				</button>
			</div>
		</div>
	);
}
